import { IClassificationTableProps } from "./ClassificationTable";
import { LegalEntityClassificationApiRow, LegalEntityClassificationViewRow, RiskLevel } from "../LegalEntityClassificationTab.types";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useState } from "react";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider";
import { EvaluationStatus } from "../../../common/components/questionnaireComponents/evaluateQuestionnaireDialog/EvaluateQuestionnaireDialog.types";
import { getQuestionnaireResponseScore } from "../../../common/questionnaireResponseScore";
import { htmlToPlainText, isNullOrWhitespace, setFirstLetterToLowerCase } from "../../../common/stringOperations";
import { useTranslation } from "../../../localization/useTranslation";
import { getLegalEntityPermissions } from "../../LegalEntity.hooks";
import { useDownloadLegalEntityClassificationReport } from "../DownloadLegalEntityClassificationReport.hooks.ts";
import { FilterOption, FilterOptionType } from "@common/components/filterPopover/FilterPopover.types.ts";
import { getUniqueFilterOptions, getScoreFilterFixedData, matchesScoreFilter } from "@common/hooks/filterUtils.ts";
import { useFilterState } from "@common/hooks/useFilterState.ts";
import { applyClientFilters, withOperators } from "@common/components/filterBar/filterOperatorMatching.ts";
import { useRowNoteDialog } from "@common/components/dotlegalNote/useRowNoteDialog";
import { EntityType } from "@common/Common.types";

export default function useClassificationTableHooks(props: IClassificationTableProps) {
    const snackbar = useDotLegalSnackbar();
    const { translateString, translateDate, translateEvaluationEnum } = useTranslation();
    const { permissions } = useUserContext();

    const [showEvaluateDialog, setShowEvaluateDialog] = useState(false);
    const [selectedClassification, setSelectedClassification] = useState<LegalEntityClassificationApiRow | undefined>(undefined);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showReopenDialog, setShowReopenDialog] = useState(false);
    const { isDownloadingPdf, onDownloadReportAsPdf, getReportPdfUrl } = useDownloadLegalEntityClassificationReport();
    const {
        selectFilters: filters,
        filterOperators,
        onFilterChange,
        pinnedFilterIds,
        onPinnedFilterIdsChange,
    } = useFilterState(props.sessionStorageKey ?? "classification-filters");

    let classifications: Array<LegalEntityClassificationApiRow> = [];

    if (props.data) {
        classifications = applyClientFilters(
            props.data.rows,
            [
                { id: "system", candidate: (r) => r.system?.id },
                { id: "legalEntity", candidate: (r) => r.legalEntity.legalEntityId },
                { id: "template", candidate: (r) => r.questionnaireId },
                {
                    id: "riskLevel",
                    candidate: (r) => (r.riskLevel !== undefined && r.riskLevel !== null ? r.riskLevel.toString() : RiskLevel.None.toString()),
                },
                { id: "submittedBy", candidate: (r) => r.submittedBy },
                { id: "evaluation", candidate: (r) => r.evaluationStatus?.toString() },
            ],
            filters,
            {},
            filterOperators,
            (row) => matchesScoreFilter(filters.score ?? [], filterOperators.score, row)
        )!;
        classifications = classifications.map((c) => {
            return {
                ...c,
                submittedBy: c.submittedBy === "deleted" ? translateString("deletedUser") : c.submittedBy,
                permissions: getLegalEntityPermissions(permissions, c.legalEntity.isGroupEntity),
            };
        });
    }

    const onClassificationDeleted = () => {
        props.refetch();
        setShowDeleteDialog(false);
    };

    function onClassificationEvaluated() {
        props.refetch();
        setSelectedClassification(undefined);
        setShowEvaluateDialog(false);
    }

    function getScore(row: LegalEntityClassificationApiRow) {
        return getQuestionnaireResponseScore(row.submitted !== undefined, row.scoreEnabled, row.score, translateString("scoreNotEnabled"));
    }

    const classificationViewRows: Array<LegalEntityClassificationViewRow> = classifications.map((c) => {
        return {
            ...c,
            systemName: c.system?.name,
            legalEntityName: c.legalEntity.name,
            scoreName: getScore(c),
            evaluationName: c.evaluationStatus ? translateEvaluationEnum(c.evaluationStatus.toString()) : "",
            createdString: c.created ? translateDate(c.created) : "",
            submittedString: c.submitted ? translateDate(c.submitted) : "",
            relatedEntitiesString: !isNullOrWhitespace(c.system?.name)
                ? c.system?.name
                : !isNullOrWhitespace(c.processingActivity?.name)
                  ? c.processingActivity?.name
                  : "",
            notePlainText: htmlToPlainText(c.note),
        };
    });

    const { getNoteMenuItem, noteDialogProps } = useRowNoteDialog<LegalEntityClassificationApiRow>({
        getPostUrl: (row) => `/legalentityclassification/${row.id}/note`,
        entityType: EntityType.LegalEntityClassification,
        onSaved: props.refetch,
    });

    function getFilterOptions(): Array<FilterOption> {
        const options: Array<FilterOption> = [
            {
                id: "template",
                label: translateString("template"),
                type: FilterOptionType.SelectField,
                value: filters.template ?? [],
                fixedData: getUniqueFilterOptions(
                    props.data?.rows,
                    (r) => r.questionnaireName,
                    (r) => r.questionnaireId
                ),
            },
        ];

        if (props.pageType === "legalEntity") {
            options.push({
                id: "system",
                label: translateString("assets"),
                type: FilterOptionType.SelectField,
                value: filters.system ?? [],
                fixedData: props.data?.selectableSystems?.map((x) => ({ id: x.id, name: x.name })) ?? [],
            });
        } else {
            options.push({
                id: "legalEntity",
                label: translateString("legalEntity"),
                type: FilterOptionType.SelectField,
                value: filters.legalEntity ?? [],
                fixedData: props.data?.selectableLegalEntities?.map((x) => ({ id: x.id, name: x.name })) ?? [],
            });
        }

        options.push(
            {
                id: "riskLevel",
                label: translateString("riskLevel"),
                type: FilterOptionType.SelectField,
                value: filters.riskLevel ?? [],
                fixedData: Object.values(RiskLevel)
                    .filter((v): v is number => typeof v === "number")
                    .map((v) => ({
                        id: v.toString(),
                        name: v === RiskLevel.None ? translateString("none") : translateString(setFirstLetterToLowerCase(RiskLevel[v])),
                    })),
            },
            {
                id: "submittedBy",
                label: translateString("submittedBy"),
                type: FilterOptionType.SelectField,
                value: filters.submittedBy ?? [],
                fixedData: getUniqueFilterOptions(props.data?.rows, (r) =>
                    r.submittedBy === "deleted" ? translateString("deletedUser") : (r.submittedBy ?? undefined)
                ),
            },
            {
                id: "evaluation",
                label: translateString("evaluation"),
                type: FilterOptionType.SelectField,
                value: filters.evaluation ?? [],
                fixedData: Object.values(EvaluationStatus)
                    .filter((v): v is number => typeof v === "number")
                    .map((v) => ({ id: v.toString(), name: translateEvaluationEnum(v.toString()) })),
            },
            {
                id: "score",
                label: translateString("score"),
                type: FilterOptionType.SelectField,
                value: filters.score ?? [],
                fixedData: getScoreFilterFixedData(translateString),
            }
        );

        return withOperators(options, filterOperators);
    }

    const hasActiveFilters = Object.values(filters).some((v) => v.length > 0);

    return {
        pinnedFilterIds,
        onPinnedFilterIdsChange,
        hasData: props.isLoading ? true : props.data!.rows.length > 0,
        classifications: classificationViewRows,
        selectedClassification,
        setSelectedClassification,
        showDeleteDialog,
        setShowDeleteDialog,
        onClassificationDeleted,
        getReportPdfUrl,
        isDownloadingPdf,
        onDownloadReportAsPdf,
        showEvaluateDialog,
        setShowEvaluateDialog,
        onClassificationEvaluated,
        showReopenDialog,
        setShowReopenDialog,
        getFilterOptions,
        onFilterChange,
        hasActiveFilters,
        getNoteMenuItem,
        noteDialogProps,
    };
}
